import { useEffect, useRef } from "react";

import { useIdle } from "@eisox/tools";
import { useQueries, type QueryFunction, type QueryKey, type UseQueryResult } from "@tanstack/react-query";

import { Mode } from "~/components/live-history-toggle";
import { LIVE_IDLE_TIMEOUT_MS, LIVE_POLL_INTERVAL_MS } from "~/helpers/history.helper";

interface UseStateSnapshotsParams<T, K extends QueryKey> {
  entities: T[];
  mode: Mode;
  historyDate: string;
  stateQuery: (id: string, date?: string) => { queryKey: K; queryFn: QueryFunction<T, K> };
  isViewed: (entity: T) => boolean;
}

interface UseStateSnapshotsResult<T> {
  stateQueries: UseQueryResult<T>[];
  /** Entities with their freshest settled snapshot merged in. */
  merged: T[];
  isHistory: boolean;
  /** A viewed entity's history snapshot is (re)fetching. */
  isSnapshotLoading: boolean;
  /** A viewed entity has no data yet. */
  viewedPending: boolean;
  /** First viewed entity's query — anchors the frozen-feed check. */
  representativeQuery?: UseQueryResult<T>;
  /** Live polling is paused (user idle) or resuming. */
  isLivePaused: boolean;
}

/** Polls every entity's `/state` (live, or at `historyDate` in History) and keeps the last settled snapshot per id. */
export function useStateSnapshots<T extends { id: string }, K extends QueryKey>({
  entities,
  mode,
  historyDate,
  stateQuery,
  isViewed,
}: UseStateSnapshotsParams<T, K>): UseStateSnapshotsResult<T> {
  const isHistory = mode === Mode.History;
  const isIdle = useIdle({ idleTime: LIVE_IDLE_TIMEOUT_MS });

  const stateQueries = useQueries({
    queries: entities.map(entity => ({
      ...stateQuery(entity.id, isHistory ? historyDate : undefined),
      refetchInterval: isHistory || isIdle ? (false as const) : LIVE_POLL_INTERVAL_MS,
      refetchOnWindowFocus: false,
      staleTime: 0,
      meta: { skipGlobalLoader: true },
    })),
  });

  // Last settled snapshot per id; reset on a Live↔History switch so modes don't bleed.
  const settledById = useRef(new Map<string, T>());
  const settledMode = useRef(mode);
  if (settledMode.current !== mode) {
    settledById.current = new Map();
    settledMode.current = mode;
  }
  stateQueries.forEach((query, index) => {
    const entity = entities[index];
    if (entity && query.data) settledById.current.set(entity.id, query.data);
  });
  const merged = entities.map(entity => settledById.current.get(entity.id) ?? entity);

  const viewedFetching = stateQueries.some((query, index) => {
    const entity = entities[index];
    return entity != null && isViewed(entity) && query.isFetching;
  });
  const viewedPending = stateQueries.some((query, index) => {
    const entity = entities[index];
    return entity != null && isViewed(entity) && query.isPending;
  });
  const repIndex = entities.findIndex(isViewed);
  const representativeQuery = repIndex === -1 ? undefined : stateQueries[repIndex];

  // Stay "paused" after idle until the resume refetch settles (data or error) so it can't trip the stale dialog.
  const repUpdatedAt = representativeQuery?.dataUpdatedAt ?? 0;
  const repErrorAt = representativeQuery?.errorUpdatedAt ?? 0;
  const wasIdle = useRef(isIdle);
  const resuming = useRef(false);
  const resumeMarker = useRef(repUpdatedAt);
  const resumeErrorMarker = useRef(repErrorAt);
  if (wasIdle.current !== isIdle) {
    wasIdle.current = isIdle;
    if (!isIdle && !isHistory) {
      resuming.current = true;
      resumeMarker.current = repUpdatedAt;
      resumeErrorMarker.current = repErrorAt;
    }
  }
  if (resuming.current && (repUpdatedAt !== resumeMarker.current || repErrorAt !== resumeErrorMarker.current)) {
    resuming.current = false;
  }

  // On wake, refetch at once instead of waiting a full interval. Skip the initial mount (already fetching).
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (!isIdle && !isHistory) stateQueries.forEach(query => void query.refetch());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only refetch when leaving idle; stateQueries identity churns every render
  }, [isIdle]);

  return {
    stateQueries,
    merged,
    isHistory,
    isSnapshotLoading: isHistory && viewedFetching,
    viewedPending,
    representativeQuery,
    isLivePaused: isIdle || resuming.current,
  };
}
